import { type FormEvent, useEffect, useRef, useState } from 'react';

import {
  ApiError,
  deleteLibraryItem,
  getLibraryDescriber,
  getLibraryItem,
  LIBRARY_DESCRIPTION_MIN_LENGTH,
  LIBRARY_KIND_LABEL,
  LIBRARY_KINDS,
  LIBRARY_SOURCES,
  LIBRARY_UPLOAD_EXTENSIONS,
  LIBRARY_UPLOAD_LIMIT_TEXT,
  type LibraryDescriberState,
  type LibraryIndexState,
  type LibraryItem,
  type LibraryKind,
  type LibrarySearchResult,
  type LibrarySource,
  type LibraryUploadAnswer,
  libraryUploadRefusal,
  type LibraryUploadWords,
  searchLibrary,
  type StorageSettings,
  suggestLibraryWords,
  updateLibraryItem,
  uploadLibraryFile,
} from '../api.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { describeError, redirectIfUnauthorised, useAppData, useKeyChords } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { Link, replaceSearch, useLocation } from '../router.tsx';
import {
  Badge,
  EmptyState,
  Facts,
  Kbd,
  PageHeader,
  Segmented,
  Skeleton,
  type Tone,
} from '../ui.tsx';

/**
 * The shared library as the operator browses it (US-023).
 *
 * Everything agents can reuse — repository pictures, generated stills and
 * clips, animations and approved renders — as one grid of thumbnails over
 * `GET /api/library`, the same search the agents run through `mc library
 * search`. The query, the kind and source chips and the repository are all in
 * the URL, so a filtered view survives a reload and can be linked to.
 *
 * The open item is in the URL too, as `?item=<id>`: the content page's "From
 * the library" strip links straight to an item's detail panel (US-028).
 *
 * **Upload** (or a file dropped anywhere on the page) puts the operator's own
 * pictures and clips in beside them (US-025), one dialog per file.
 */

/** How long the search box waits after the last keystroke before it searches. */
const SEARCH_DEBOUNCE_MS = 300;

/** The server's `LIBRARY_SEARCH_MAX_LIMIT`: one page is the whole grid. */
const PAGE_LIMIT = 100;

const SOURCE_LABEL: Record<LibrarySource, string> = {
  repository: 'Repository',
  generated: 'Generated',
  animation: 'Animated',
  render: 'Approved render',
  upload: 'Upload',
};

const SOURCE_TONE: Record<LibrarySource, Tone> = {
  repository: 'neutral',
  generated: 'active',
  animation: 'review',
  render: 'done',
  upload: 'wait',
};

function isKind(value: string | null): value is LibraryKind {
  return (LIBRARY_KINDS as readonly string[]).includes(value ?? '');
}

function isSource(value: string | null): value is LibrarySource {
  return (LIBRARY_SOURCES as readonly string[]).includes(value ?? '');
}

/**
 * Whether an item plays. A render is both the video and its poster, told apart
 * only by the poster having no duration.
 */
function isMoving(item: LibraryItem): boolean {
  if (item.kind === 'video' || item.kind === 'animation') return true;
  return item.kind === 'render' && item.durationMs !== null;
}

function seconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`;
}

function dimensions(item: LibraryItem): string | null {
  return item.width === null || item.height === null
    ? null
    : `${String(item.width)} × ${String(item.height)}`;
}

export function Library() {
  const { repositories } = useAppData();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const q = params.get('q') ?? '';
  const kindParam = params.get('kind');
  const kind: LibraryKind | 'all' = isKind(kindParam) ? kindParam : 'all';
  const sourceParam = params.get('source');
  const source: LibrarySource | 'all' = isSource(sourceParam) ? sourceParam : 'all';
  const repositoryId = params.get('repositoryId') ?? '';
  /** The item whose detail panel is open, so a link can open it (US-028). */
  const itemParam = params.get('item');
  const [query, setQuery] = useState(q);
  const [result, setResult] = useState<LibrarySearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<LibraryItem | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  /** Files chosen or dropped, the first of them in the upload dialog. */
  const [queue, setQueue] = useState<readonly File[]>([]);
  const [notes, setNotes] = useState<readonly UploadNote[]>([]);
  const [dragging, setDragging] = useState(false);
  const [describer, setDescriber] = useState<LibraryDescriberState | null>(null);

  // Whether Suggest description can be offered: asked once, as the page opens.
  useEffect(() => {
    const controller = new AbortController();
    getLibraryDescriber(controller.signal)
      .then(setDescriber)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        redirectIfUnauthorised(cause);
      });
    return () => {
      controller.abort();
    };
  }, []);

  /** Queues what the library takes; says why about the rest, naming the limit. */
  const take = (files: Iterable<File>): void => {
    const accepted: File[] = [];
    const refused: UploadNote[] = [];
    for (const file of files) {
      const refusal = libraryUploadRefusal(file);
      if (refusal === null) accepted.push(file);
      else refused.push({ key: `${file.name}:${String(Date.now())}`, tone: 'error', text: refusal });
    }
    setNotes(refused);
    setQueue((current) => [...current, ...accepted]);
  };

  const uploaded = (file: File, answer: LibraryUploadAnswer): void => {
    const { item } = answer;
    setNotes((current) => [
      {
        key: `${item.id}:${String(Date.now())}`,
        tone: answer.existed ? 'info' : 'ok',
        text: answer.existed
          ? `${file.name} is already in the library, as “${item.title}”.`
          : `${file.name} was added to the library as “${item.title}”.`,
        item,
      },
      ...current,
    ]);
    if (!answer.existed) {
      setResult((current) =>
        current === null || current.items.some((candidate) => candidate.id === item.id)
          ? current
          : { ...current, items: [item, ...current.items] },
      );
    }
    setQueue((current) => current.slice(1));
  };

  useKeyChords({ '/': () => searchRef.current?.focus() });

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(window.location.search);
    if (value === '' || value === 'all') next.delete(key);
    else next.set(key, value);
    replaceSearch(next);
  };

  const openItem = (item: LibraryItem): void => {
    setOpen(item);
    setParam('item', item.id);
  };

  const closeItem = (): void => {
    setOpen(null);
    setParam('item', '');
  };

  // The panel follows `?item=`: an item named there that is not the one open
  // is asked for by id, since it need not be on the grid's page at all.
  useEffect(() => {
    if (itemParam === null) {
      setOpen(null);
      return;
    }
    if (open?.id === itemParam) return;
    const controller = new AbortController();
    getLibraryItem(itemParam, controller.signal)
      .then((answer) => {
        setOpen(answer.item);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        setNotes((current) => [
          {
            key: `missing:${itemParam}`,
            tone: 'error',
            text:
              cause instanceof ApiError && cause.status === 404
                ? 'That item is no longer in the library.'
                : describeError(cause),
          },
          ...current,
        ]);
        setParam('item', '');
      });
    return () => {
      controller.abort();
    };
  }, [itemParam]);

  // The box writes to the URL once typing has stopped for a beat, and the
  // search below follows the URL: one request per pause, not per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setParam('q', query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    searchLibrary(
      {
        q,
        kind: kind === 'all' ? undefined : kind,
        source: source === 'all' ? undefined : source,
        repositoryId,
        limit: PAGE_LIMIT,
      },
      controller.signal,
    )
      .then((next) => {
        setResult(next);
        setError(null);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
        setLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [q, kind, source, repositoryId]);

  const filtered = q !== '' || kind !== 'all' || source !== 'all' || repositoryId !== '';
  const items = result?.items ?? [];

  return (
    <div
      className={dragging ? 'page library--dropping' : 'page'}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setDragging(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(event) => {
        if (event.dataTransfer.files.length === 0) return;
        event.preventDefault();
        setDragging(false);
        take(Array.from(event.dataTransfer.files));
      }}
    >
      <PageHeader
        title="Library"
        subtitle="Every picture and clip the agents can reuse instead of generating it again."
        actions={
          <>
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                fileRef.current?.click();
              }}
            >
              <Icon name="plus" />
              Upload
            </button>
            <input
              ref={fileRef}
              className="visually-hidden"
              type="file"
              multiple
              accept={UPLOAD_ACCEPT}
              tabIndex={-1}
              aria-hidden="true"
              onChange={(event) => {
                take(Array.from(event.target.files ?? []));
                // The same file chosen again is a change too.
                event.target.value = '';
              }}
            />
          </>
        }
      />
      <p className="muted library__note library__drop-hint">
        Drop files anywhere on this page to upload them. {LIBRARY_UPLOAD_LIMIT_TEXT}
      </p>

      {notes.map((note) => (
        <div
          key={note.key}
          className={`notice notice--${note.tone}`}
          role={note.tone === 'error' ? 'alert' : 'status'}
        >
          <Icon name={note.tone === 'error' ? 'x-circle' : note.tone === 'ok' ? 'check-circle' : 'info'} />
          <div className="notice__body">
            {note.text}{' '}
            {note.item !== undefined && (
              <button
                type="button"
                className="link link--button"
                onClick={() => {
                  if (note.item !== undefined) openItem(note.item);
                }}
              >
                Open it
              </button>
            )}
          </div>
        </div>
      ))}

      <div className="toolbar">
        <label className="search">
          <Icon name="search" />
          <input
            ref={searchRef}
            className="search__input"
            type="search"
            placeholder="Search by what it shows"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            aria-label="Search the library"
          />
          <Kbd>/</Kbd>
        </label>
        <select
          className="field__input field__input--inline"
          value={repositoryId}
          onChange={(event) => {
            setParam('repositoryId', event.target.value);
          }}
          aria-label="Repository"
        >
          <option value="">All repositories</option>
          {(repositories ?? []).map((repository) => (
            <option key={repository.id} value={repository.id}>
              {repository.name}
            </option>
          ))}
        </select>
        <Segmented
          ariaLabel="Kind"
          value={kind}
          onChange={(value) => {
            setParam('kind', value);
          }}
          options={[
            { value: 'all', label: 'Any kind' },
            ...LIBRARY_KINDS.map((value) => ({ value, label: LIBRARY_KIND_LABEL[value] })),
          ]}
        />
        <Segmented
          ariaLabel="Source"
          value={source}
          onChange={(value) => {
            setParam('source', value);
          }}
          options={[
            { value: 'all', label: 'Any source' },
            ...LIBRARY_SOURCES.map((value) => ({ value, label: SOURCE_LABEL[value] })),
          ]}
        />
      </div>

      {error !== null && (
        <div className="notice notice--error" role="alert">
          <Icon name="x-circle" />
          <div className="notice__body">Could not search the library: {error}</div>
        </div>
      )}

      {result === null ? (
        error === null && (
          <div className="panel">
            <div className="panel__body">
              <Skeleton lines={4} />
            </div>
          </div>
        )
      ) : items.length === 0 && !filtered ? (
        <EmptyState icon="stack" title="The library is empty">
          Items appear here as contents are produced — pictures taken from repositories, generated
          stills and clips, animations and approved renders — or as files are uploaded.
        </EmptyState>
      ) : items.length === 0 ? (
        <EmptyState icon="filter" title="Nothing matches">
          No item matches this search.{' '}
          <button
            type="button"
            className="link link--button"
            onClick={() => {
              setQuery('');
              replaceSearch(new URLSearchParams());
            }}
          >
            Clear filters
          </button>
        </EmptyState>
      ) : (
        <>
          {q !== '' && !result.semantic && (
            <p className="muted library__note">
              Matched on words only: semantic search needs an OpenRouter key and embedded items.
            </p>
          )}
          <ul className="gallery" aria-busy={loading}>
            {items.map((item) => (
              <LibraryTile
                key={item.id}
                item={item}
                onOpen={() => {
                  openItem(item);
                }}
              />
            ))}
          </ul>
        </>
      )}

      <LibraryDetail
        item={open}
        repositories={repositories ?? []}
        onClose={closeItem}
        onSaved={(saved) => {
          setOpen(saved);
          setResult((current) =>
            current === null
              ? current
              : {
                  ...current,
                  items: current.items.map((candidate) =>
                    candidate.id === saved.id ? { ...saved, score: candidate.score } : candidate,
                  ),
                },
          );
        }}
        onDeleted={(id) => {
          closeItem();
          setNotes((current) => current.filter((note) => note.item?.id !== id));
          setResult((current) =>
            current === null
              ? current
              : { ...current, items: current.items.filter((candidate) => candidate.id !== id) },
          );
        }}
      />

      <LibraryUploadDialog
        file={queue[0] ?? null}
        remaining={Math.max(0, queue.length - 1)}
        describer={describer}
        onUploaded={uploaded}
        onSkip={() => {
          setQueue((current) => current.slice(1));
        }}
        onClose={() => {
          setQueue([]);
        }}
      />

      {dragging && (
        <div className="library__drop" aria-hidden="true">
          <Icon name="plus" />
          Drop to upload to the library
        </div>
      )}
    </div>
  );
}

/** What an upload came to, shown above the grid. */
interface UploadNote {
  readonly key: string;
  readonly tone: 'ok' | 'info' | 'error';
  readonly text: string;
  /** The item it made or found, which the note offers to open. */
  readonly item?: LibraryItem;
}

function KindBadge({ item }: { readonly item: LibraryItem }) {
  return <Badge>{LIBRARY_KIND_LABEL[item.kind]}</Badge>;
}

function SourceBadge({ item }: { readonly item: LibraryItem }) {
  return <Badge tone={SOURCE_TONE[item.source]}>{SOURCE_LABEL[item.source]}</Badge>;
}

function LibraryTile({ item, onOpen }: { readonly item: LibraryItem; readonly onOpen: () => void }) {
  const moving = isMoving(item);
  return (
    <li className="tile">
      <button
        type="button"
        className="tile__thumb"
        onClick={onOpen}
        aria-label={`Open ${item.title}`}
        title={item.description}
      >
        {item.previewUrl === null ? (
          <Icon name={moving ? 'device-camera-video' : 'image'} className="tile__icon" />
        ) : moving ? (
          // No poster is stored for a clip, so its first frame is the poster:
          // `preload="metadata"` plus a media fragment just past zero makes
          // the browser decode and paint that one frame and nothing more.
          <video
            className="tile__image"
            src={`${item.previewUrl}#t=0.1`}
            preload="metadata"
            muted
            playsInline
            aria-hidden="true"
          />
        ) : (
          <img className="tile__image" src={item.previewUrl} alt="" loading="lazy" />
        )}
      </button>
      <div className="tile__body">
        <p className="tile__name" title={item.title}>
          {item.title}
        </p>
        <p className="tile__badges">
          <KindBadge item={item} />
          <SourceBadge item={item} />
        </p>
      </div>
    </li>
  );
}

/** How often an open item asks whether its re-embed has landed. */
const INDEX_POLL_MS = 2000;

/**
 * One item, full size, with everything known about where it came from — and
 * the two corrections the operator can make to it (US-024): new words, which
 * the search index then catches up with, or removing it from the library.
 */
function LibraryDetail({
  item,
  repositories,
  onClose,
  onSaved,
  onDeleted,
}: {
  readonly item: LibraryItem | null;
  readonly repositories: readonly { readonly name: string; readonly githubSlug: string | null }[];
  readonly onClose: () => void;
  readonly onSaved: (item: LibraryItem) => void;
  readonly onDeleted: (id: string) => void;
}) {
  const { storage } = useAppData();
  const ref = useRef<HTMLDialogElement>(null);
  const [editing, setEditing] = useState(false);
  const [index, setIndex] = useState<LibraryIndexState | null>(null);
  /** Bumped by every answer, so an answer of "still updating" asks again. */
  const [asked, setAsked] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const id = item?.id ?? null;

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (item !== null && !dialog.open) dialog.showModal();
    if (item === null && dialog.open) dialog.close();
  }, [item]);

  // Another item is another form: nothing half-typed carries over.
  useEffect(() => {
    setEditing(false);
    setIndex(null);
    setConfirming(false);
    setDeleteError(null);
  }, [id]);

  // Where the open item stands in the index, asked once on opening and then
  // every couple of seconds for as long as a re-embed is still out.
  useEffect(() => {
    if (id === null || (index !== null && index !== 'updating')) return;
    const controller = new AbortController();
    const ask = (): void => {
      getLibraryItem(id, controller.signal)
        .then((answer) => {
          setIndex(answer.index);
          setAsked((count) => count + 1);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted) return;
          redirectIfUnauthorised(cause);
        });
    };
    const timer = window.setTimeout(ask, index === null ? 0 : INDEX_POLL_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [id, index, asked]);

  const remove = (): void => {
    if (item === null) return;
    setDeleting(true);
    setDeleteError(null);
    deleteLibraryItem(item.id)
      .then(() => {
        setDeleting(false);
        setConfirming(false);
        onDeleted(item.id);
      })
      .catch((cause: unknown) => {
        setDeleting(false);
        if (redirectIfUnauthorised(cause)) return;
        setDeleteError(describeError(cause));
      });
  };

  const repository =
    item?.repository === null || item === null
      ? null
      : (repositories.find((candidate) => candidate.name === item.repository) ?? null);

  return (
    <>
      <dialog
        ref={ref}
        className="lightbox lightbox--library"
        aria-label={item?.title ?? 'Library item'}
        onClose={onClose}
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        {item !== null && (
          <>
            <div className="lightbox__head">
              <p className="lightbox__title">{item.title}</p>
              <button type="button" className="button button--quiet" onClick={onClose}>
                <Icon name="x" />
                Close
              </button>
            </div>
            <div className="lightbox__stage">
              {item.previewUrl === null ? (
                <p className="lightbox__note">
                  No preview: configure storage in Settings to see library files here.
                </p>
              ) : isMoving(item) ? (
                <video
                  className="lightbox__video"
                  src={item.previewUrl}
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img className="lightbox__image" src={item.previewUrl} alt={item.title} />
              )}
            </div>
            <div className="library__detail">
              <div className="library__actions">
                <p className="tile__badges">
                  <KindBadge item={item} />
                  <SourceBadge item={item} />
                  <IndexBadge index={index} />
                </p>
                {!editing && (
                  <span className="library__buttons">
                    <button
                      type="button"
                      className="button button--quiet"
                      onClick={() => {
                        setEditing(true);
                      }}
                    >
                      <Icon name="pencil" />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="button button--quiet button--danger"
                      onClick={() => {
                        setDeleteError(null);
                        setConfirming(true);
                      }}
                    >
                      <Icon name="trash" />
                      Delete
                    </button>
                  </span>
                )}
              </div>
              {editing ? (
                <LibraryEditForm
                  item={item}
                  onCancel={() => {
                    setEditing(false);
                  }}
                  onSaved={(answer) => {
                    setEditing(false);
                    setIndex(answer.index);
                    onSaved(answer.item);
                  }}
                />
              ) : (
                <>
                  <p className="library__description">{item.description}</p>
                  {item.tags.length > 0 && (
                    <ul className="library__tags" aria-label="Tags">
                      {item.tags.map((tag) => (
                        <li key={tag}>
                          <Badge>{tag}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              <Facts
                items={[
                  { label: 'Dimensions', value: dimensions(item) ?? '—' },
                  {
                    label: 'Duration',
                    value: item.durationMs === null ? '—' : seconds(item.durationMs),
                  },
                  { label: 'Licence', value: item.licence ?? '—' },
                  { label: 'Attribution', value: item.attribution ?? '—' },
                  {
                    label: 'Content',
                    value:
                      item.contentId === null ? (
                        '—'
                      ) : (
                        <Link
                          className="link"
                          href={`/content/${encodeURIComponent(item.contentId)}`}
                        >
                          Open the content
                        </Link>
                      ),
                  },
                  {
                    label: 'Repository',
                    value:
                      item.repository === null ? (
                        '—'
                      ) : repository?.githubSlug !== null && repository !== null ? (
                        <a
                          className="link"
                          href={`https://github.com/${repository.githubSlug}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.repository} <Icon name="link-external" />
                        </a>
                      ) : repository !== null ? (
                        <Link className="link" href="/repositories">
                          {item.repository}
                        </Link>
                      ) : (
                        `${item.repository} (removed)`
                      ),
                  },
                  { label: 'Id', value: `library:${item.id}`, mono: true },
                  { label: 'Added', value: new Date(item.createdAt).toLocaleString() },
                ]}
              />
            </div>
          </>
        )}
      </dialog>

      <ConfirmDialog
        open={confirming && item !== null}
        title={item === null ? 'Delete this item?' : `Delete “${item.title}”?`}
        confirmLabel="Delete item"
        busyLabel="Deleting…"
        busy={deleting}
        danger
        onConfirm={remove}
        onCancel={() => {
          setConfirming(false);
        }}
      >
        <p>
          It leaves the library: agents no longer find it, and a plan that names{' '}
          <code>library:{item?.id}</code> is refused at Mark ready.
        </p>
        <LibraryBucketWarning storage={storage} />
        <p>
          Contents that already copied it keep their copy: nothing in their workspaces, assets or
          deliveries changes.
        </p>
        {deleteError !== null && (
          <div className="notice notice--error" role="alert">
            <Icon name="x-circle" />
            <div className="notice__body">Could not delete it: {deleteError}</div>
          </div>
        )}
      </ConfirmDialog>
    </>
  );
}

/**
 * What happens to the item's object, in the words of what happens: whether
 * "Keep copies on delete" leaves it in the bucket or not.
 */
function LibraryBucketWarning({ storage }: { readonly storage: StorageSettings | null }) {
  if (storage === null) return <p className="muted">Checking what happens in the bucket…</p>;
  if (!storage.configured) {
    return <p>No bucket is configured, so there is no object to remove.</p>;
  }
  if (storage.keepCopiesOnDelete) {
    return (
      <p>
        <strong>Its file stays in {storage.bucket}.</strong> “Keep copies on delete” is on, so the
        object is left where it is and any link to it keeps working. Removing it is then yours to do
        in the bucket.
      </p>
    );
  }
  return (
    <p>
      <strong>Its file is removed from {storage.bucket}.</strong> “Keep copies on delete” is off, so
      the object goes with the item and any link to it stops working.
    </p>
  );
}

/** Where the item stands in the search index; nothing at all while it is current. */
function IndexBadge({ index }: { readonly index: LibraryIndexState | null }) {
  if (index === 'updating') {
    return (
      <span role="status">
        <Badge tone="active" pulse>
          Search index updating
        </Badge>
      </span>
    );
  }
  if (index === 'unavailable') {
    return (
      <Badge tone="wait" title="No OpenRouter key is saved, so search matches this item on its words only.">
        Words only
      </Badge>
    );
  }
  return null;
}

type EditField = 'title' | 'description' | 'tags';

/** The server's refusal codes, by the field they are about. */
const EDIT_ERROR_FIELD: Readonly<Record<string, EditField>> = {
  invalid_title: 'title',
  description_too_short: 'description',
  invalid_tags: 'tags',
};

function tagsFrom(text: string): string[] {
  return [
    ...new Set(
      text
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => tag !== ''),
    ),
  ];
}
/**
 * Title, description and tags, saved with `PATCH /api/library/:id`. A
 * description the server would refuse is refused here first, inline, and
 * nothing is sent.
 */
function LibraryEditForm({
  item,
  onCancel,
  onSaved,
}: {
  readonly item: LibraryItem;
  readonly onCancel: () => void;
  readonly onSaved: (answer: { item: LibraryItem; index: LibraryIndexState }) => void;
}) {
  return (
    <LibraryWordsForm
      idPrefix="library"
      initial={{ title: item.title, description: item.description, tags: item.tags }}
      submitLabel="Save"
      busyLabel="Saving…"
      failure="Could not save"
      onCancel={onCancel}
      onSubmit={(words) => updateLibraryItem(item.id, words).then(onSaved)}
    />
  );
}

/**
 * The words every library item carries, as one form: the edit panel's
 * (US-024) and the upload dialog's (US-025). A title and a description of at
 * least {@link LIBRARY_DESCRIPTION_MIN_LENGTH} characters are required and
 * checked here before anything is sent; a refusal the server answers anyway
 * lands under the field it is about.
 */
function LibraryWordsForm({
  idPrefix,
  initial,
  submitLabel,
  busyLabel,
  failure,
  cancelLabel = 'Cancel',
  onSubmit,
  onCancel,
  suggest,
}: {
  readonly idPrefix: string;
  readonly initial: LibraryUploadWords | Readonly<{ title: string; description: string; tags: readonly string[] }>;
  readonly submitLabel: string;
  readonly busyLabel: string;
  /** What the form-wide error starts with, e.g. "Could not save". */
  readonly failure: string;
  readonly cancelLabel?: string;
  readonly onSubmit: (words: LibraryUploadWords) => Promise<unknown>;
  readonly onCancel: () => void;
  /** Words the describer read off the file; absent hides **Suggest description**. */
  readonly suggest?: () => Promise<LibraryUploadWords>;
}) {
  const [title, setTitle] = useState(initial.title);
  const [description, setDescription] = useState(initial.description);
  const [tags, setTags] = useState(initial.tags.join(', '));
  const [errors, setErrors] = useState<Partial<Record<EditField | 'form', string>>>({});
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const next: Partial<Record<EditField, string>> = {};
    if (title.trim() === '') next.title = 'A title is required: a few words naming what it shows.';
    const length = description.trim().length;
    if (length < LIBRARY_DESCRIPTION_MIN_LENGTH) {
      next.description =
        `At least ${String(LIBRARY_DESCRIPTION_MIN_LENGTH)} characters, so agents can find it ` +
        `(this one has ${String(length)}).`;
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaving(true);
    onSubmit({ title: title.trim(), description: description.trim(), tags: tagsFrom(tags) })
      .then(() => {
        setSaving(false);
      })
      .catch((cause: unknown) => {
        setSaving(false);
        if (redirectIfUnauthorised(cause)) return;
        const field = cause instanceof ApiError ? EDIT_ERROR_FIELD[cause.code] : undefined;
        setErrors({ [field ?? 'form']: describeError(cause) });
      });
  };

  const fill = (): void => {
    if (suggest === undefined) return;
    setSuggesting(true);
    suggest()
      .then((words) => {
        setSuggesting(false);
        setDescription(words.description);
        if (title.trim() === '') setTitle(words.title);
        if (tags.trim() === '') setTags(words.tags.join(', '));
        setErrors({});
      })
      .catch((cause: unknown) => {
        setSuggesting(false);
        if (redirectIfUnauthorised(cause)) return;
        setErrors({ description: `Could not suggest one: ${describeError(cause)}` });
      });
  };

  const id = (field: string): string => `${idPrefix}-${field}`;
  const fieldError = (field: EditField) =>
    errors[field] === undefined ? null : (
      <p className="library__error" id={id(`${field}-error`)} role="alert">
        {errors[field]}
      </p>
    );

  return (
    <form className="library__form" onSubmit={submit} noValidate>
      <div className="field">
        <label className="field__label" htmlFor={id('title')}>
          Title
        </label>
        <input
          id={id('title')}
          className="field__input"
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
          aria-invalid={errors.title !== undefined}
          aria-describedby={errors.title === undefined ? undefined : id('title-error')}
          autoComplete="off"
        />
        {fieldError('title')}
      </div>
      <div className="field">
        <span className="library__label-row">
          <label className="field__label" htmlFor={id('description')}>
            Description
          </label>
          {suggest !== undefined && (
            <button
              type="button"
              className="button button--quiet button--small"
              onClick={fill}
              disabled={suggesting || saving}
            >
              <Icon name="zap" />
              {suggesting ? 'Suggesting…' : 'Suggest description'}
            </button>
          )}
        </span>
        <textarea
          id={id('description')}
          className="field__input"
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
          rows={4}
          aria-invalid={errors.description !== undefined}
          aria-describedby={
            errors.description === undefined ? id('description-hint') : id('description-error')
          }
        />
        {fieldError('description') ?? (
          <p className="field__hint" id={id('description-hint')}>
            What is visible — subject, style, colours, background, and the motion for a clip — and
            what it is for, in at least {String(LIBRARY_DESCRIPTION_MIN_LENGTH)} characters. Agents
            search on these words.
          </p>
        )}
      </div>
      <div className="field">
        <label className="field__label" htmlFor={id('tags')}>
          Tags <span className="muted">(optional, comma-separated)</span>
        </label>
        <input
          id={id('tags')}
          className="field__input"
          value={tags}
          onChange={(event) => {
            setTags(event.target.value);
          }}
          aria-invalid={errors.tags !== undefined}
          autoComplete="off"
        />
        {fieldError('tags')}
      </div>
      {errors.form !== undefined && (
        <div className="notice notice--error" role="alert">
          <Icon name="x-circle" />
          <div className="notice__body">
            {failure}: {errors.form}
          </div>
        </div>
      )}
      <div className="library__buttons">
        <button type="submit" className="button button--primary" disabled={saving || suggesting}>
          {saving ? busyLabel : submitLabel}
        </button>
        <button type="button" className="button button--quiet" onClick={onCancel} disabled={saving}>
          {cancelLabel}
        </button>
      </div>
    </form>
  );
}

/** What the file picker offers: the seven types, by extension and by MIME type. */
const UPLOAD_ACCEPT = [
  ...LIBRARY_UPLOAD_EXTENSIONS,
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/svg+xml',
  'video/mp4',
  'video/webm',
].join(',');

function megabytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** The title a file starts with: its name without the extension, in words. */
function titleOf(file: File): string {
  const dot = file.name.lastIndexOf('.');
  return (dot > 0 ? file.name.slice(0, dot) : file.name).replace(/[-_]+/g, ' ').trim();
}

/**
 * One chosen or dropped file at a time (US-025): a preview, and the words
 * every item needs before it goes in. The file is the request body of
 * `POST /api/library`; the server answers the item, or the one that already
 * holds the same bytes.
 */
function LibraryUploadDialog({
  file,
  remaining,
  describer,
  onUploaded,
  onSkip,
  onClose,
}: {
  readonly file: File | null;
  /** How many more files are queued behind this one. */
  readonly remaining: number;
  readonly describer: LibraryDescriberState | null;
  readonly onUploaded: (file: File, answer: LibraryUploadAnswer) => void;
  readonly onSkip: () => void;
  readonly onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (file !== null && !dialog.open) dialog.showModal();
    if (file === null && dialog.open) dialog.close();
  }, [file]);

  useEffect(() => {
    if (file === null) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const moving = file !== null && file.type.startsWith('video/');

  return (
    <dialog
      ref={ref}
      className="lightbox lightbox--library"
      aria-label="Upload to the library"
      onClose={onClose}
    >
      {file !== null && (
        <>
          <div className="lightbox__head">
            <p className="lightbox__title">
              Upload {file.name}
              {remaining > 0 && <span className="muted"> · {String(remaining)} more after this</span>}
            </p>
            <button type="button" className="button button--quiet" onClick={onClose}>
              <Icon name="x" />
              Close
            </button>
          </div>
          <div className="lightbox__stage">
            {preview === null ? null : moving ? (
              <video className="lightbox__video" src={preview} controls playsInline muted />
            ) : (
              <img className="lightbox__image" src={preview} alt="" />
            )}
          </div>
          <div className="library__detail">
            <p className="muted library__note">
              {megabytes(file.size)} · kept in the bucket under <code>library/</code>, apart from
              every content, and found by agents through these words.
            </p>
            <LibraryWordsForm
              // A new file is a new form: nothing typed for the last one carries over.
              key={`${file.name}:${String(file.size)}:${String(file.lastModified)}`}
              idPrefix="library-upload"
              initial={{ title: titleOf(file), description: '', tags: [] }}
              submitLabel="Upload"
              busyLabel="Uploading…"
              failure="Could not upload"
              cancelLabel={remaining > 0 ? 'Skip this file' : 'Cancel'}
              onCancel={onSkip}
              onSubmit={(words) =>
                uploadLibraryFile(file, words).then((answer) => {
                  onUploaded(file, answer);
                })
              }
              {...(describer?.available === true
                ? { suggest: () => suggestLibraryWords(file) }
                : {})}
            />
          </div>
        </>
      )}
    </dialog>
  );
}
